import {
  PushDestination,
  PushServiceType,
  PushEventType,
  PushScope,
} from '../../shared/interfaces/push-destination.interface';

/**
 * View model for one row of the push-destinations list. Produced purely from the
 * API shape ({@link PushDestination}) — no component or service state — so it is
 * trivially unit-testable. The template binds the precomputed fields directly
 * (icon, label, event chips, status) rather than calling component methods.
 *
 * Mirrors the connections {@link ConnectionDisplay} pattern (SCRUM-761).
 */
export interface PushDestinationDisplay {
  destinationId: number;
  name: string;
  /** Raw service type, retained for the `service-{type}` CSS hook. */
  serviceType: PushServiceType;
  /** Human label, e.g. `Slack`. */
  serviceLabel: string;
  /** Material icon name for the service badge. */
  serviceIcon: string;
  /** Raw event list, retained for ordering/tracking. */
  events: PushEventType[];
  /** Precomputed event chips: raw type (for the `event-{type}` CSS hook) + label. */
  eventChips: PushDestinationEventChip[];
  scope: PushScope;
  /** Human scope label: `Project` / `Personal`. */
  scopeLabel: string;
  enabled: boolean;
  /** Human status label: `Enabled` / `Disabled`. */
  statusLabel: string;
  originalData: PushDestination;
}

export interface PushDestinationEventChip {
  type: PushEventType;
  label: string;
}

const SERVICE_ICONS: Record<PushServiceType, string> = {
  slack: 'tag',
  teams: 'groups',
  zapier: 'bolt',
  generic: 'webhook',
  email: 'email',
};

const SERVICE_LABELS: Record<PushServiceType, string> = {
  slack: 'Slack',
  teams: 'Teams',
  zapier: 'Zapier',
  generic: 'Generic',
  email: 'Email',
};

const EVENT_LABELS: Record<PushEventType, string> = {
  JobCompleted: 'Completed',
  JobFailed: 'Failed',
  JobNeedsReview: 'Needs Review',
  Manual: 'Manual',
};

/** Material icon for a service type; falls back to a generic bell. */
export function getServiceIcon(serviceType: PushServiceType): string {
  return SERVICE_ICONS[serviceType] ?? 'notifications';
}

/** Human label for a service type; falls back to the raw value. */
export function getServiceLabel(serviceType: PushServiceType): string {
  return SERVICE_LABELS[serviceType] ?? serviceType;
}

/** Human label for an event type; falls back to the raw value. */
export function getEventLabel(event: PushEventType): string {
  return EVENT_LABELS[event] ?? event;
}

/** Human label for a scope. */
export function getScopeLabel(scope: PushScope): string {
  return scope === 'personal' ? 'Personal' : 'Project';
}

/** Maps a single push destination to its display row. */
export function mapPushDestinationToDisplay(
  destination: PushDestination,
): PushDestinationDisplay {
  return {
    destinationId: destination.destinationId,
    name: destination.name,
    serviceType: destination.serviceType,
    serviceLabel: getServiceLabel(destination.serviceType),
    serviceIcon: getServiceIcon(destination.serviceType),
    events: destination.events,
    eventChips: destination.events.map((event) => ({
      type: event,
      label: getEventLabel(event),
    })),
    scope: destination.scope,
    scopeLabel: getScopeLabel(destination.scope),
    enabled: destination.enabled,
    statusLabel: destination.enabled ? 'Enabled' : 'Disabled',
    originalData: destination,
  };
}

/** Builds the full display list from the API list. */
export function buildPushDestinationDisplays(
  destinations: PushDestination[],
): PushDestinationDisplay[] {
  return destinations.map(mapPushDestinationToDisplay);
}
